import { isEmpty } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { findRowsList } from '../../../actions/agGrid';
import {
  maxResultPerFilter,
  notificationType,
} from '../../../constants/globals';
import { userActions } from '../../../constants/user-actions';
import AuthContext from '../../../context/auth_context';
import '../../../styles/css/Attribute.css';
import withTranslations from '../../../utils/HighOrderComponent';
import ModalDataWrapper from '../../Administration/ModalDataWrapper';
import Alert from '../Alert';
import InputField from '../InputField';
import AttributeItem from './AttributeItem';
import AttributeCardFooter from './AttributeViewItems/AttributeCardFooter';
import AttributeCheckBox from './AttributeViewItems/AttributeCheckBox';
import FilterCard from './FilterCard';
import {
  filterAttributeStatuses,
  mapFilters,
  prepareCriteriaForBE,
  radioOptionButtons,
  toLowerText,
} from './attribute-utils';

const AttributeView = (props) => {
  const {
      gridRef,
      t,
      enums,
      tableName,
      setRefreshState,
      refreshState,
      collapse,
      attributeList = [],
      inForm,
      additionalFilterCriteria = [],
    } = props || {},
    { Placeholders, Errors, NotificationMessages } = t || {},
    { AttributeFilters, AttributeRadioButton } = enums || {};
  const initOpenState = collapse ? '-1' : '0';
  const dispatch = useDispatch();
  const { allowedActions } = useContext(AuthContext);
  const [filteredAttributes, setFilteredAttributes] = useState(attributeList);
  const [maxResultFilter, setMaxResultFilter] = useState(maxResultPerFilter[0]);
  const [openedFilter, setOpenedFilter] = useState(null);
  const [activeKey, setActiveKey] = useState(initOpenState);
  const [attributeStates, setAttributeStates] = useState([]);

  const finalAttributeList = filteredAttributes || attributeList;

  const columnsPerRow = finalAttributeList.length > 1 ? 2 : 1;
  const showEditTableFiltersButton = allowedActions?.includes(
    userActions.ADMINISTRATION_FILTER_UPDATE
  );

  const cleanUpAttributes = useCallback(() => {
    setAttributeStates([]);
    setActiveKey(initOpenState);
  }, [initOpenState]);

  useEffect(() => {
    setAttributeStates([]);
  }, [tableName, NotificationMessages, refreshState]);

  useEffect(() => {
    setFilteredAttributes(attributeList);
  }, [attributeList]);

  useEffect(() => {
    cleanUpAttributes();
    return () => {
      setMaxResultFilter(0);
      setAttributeStates([]);
    };
  }, [tableName, cleanUpAttributes]);

  const onCheckBoxChangeHandler = (attribute) => {
    const newObj = {
      attribute: attribute || null,
      firstCriteria:
        filterAttributeStatuses(AttributeFilters, attribute)[0] || null,
      firstInput: null,
      firstInputRange: null,
      secondCriteria:
        filterAttributeStatuses(AttributeFilters, attribute)[0] || null,
      secondInput: null,
      secondInputRange: null,
      radioOption: radioOptionButtons(AttributeRadioButton)[0]?.value || null,
      error: null,
    };
    setAttributeStates((prevState) => {
      if (
        prevState?.find((item) => attribute?.label === item?.attribute?.label)
      ) {
        return prevState?.filter(
          (item) => item?.attribute?.label !== attribute?.label
        );
      }
      return prevState.concat([newObj]);
    });
  };

  const onChangeFilterHandler = (event) => {
    if (event.target.value) {
      const filteredList = attributeList?.filter((attribute) =>
        toLowerText(attribute?.label).includes(toLowerText(event.target.value))
      );
      setFilteredAttributes(filteredList);
    } else {
      setFilteredAttributes(attributeList);
    }
  };

  const onChangeMaxResultFilterHandler = (event) => {
    setMaxResultFilter(event.target.value);
  };

  const onToggleHandler = () => {
    const newValue = activeKey !== '0' ? '0' : '-1';
    setActiveKey(newValue);
  };

  const filterValidation = () => {
    const filtersWithOptionalErrors = mapFilters(
      attributeStates,
      Errors,
      AttributeFilters
    );
    setAttributeStates(filtersWithOptionalErrors);
    return !!filtersWithOptionalErrors?.find((item) => item.error);
  };

  const filterAction = (finalFilter, maxResultFilter) => {
    gridRef?.current?.api?.showLoadingOverlay();
    dispatch(
      findRowsList(
        tableName,
        finalFilter,
        maxResultFilter,
        NotificationMessages,
        gridRef,
        inForm
      )
    );
  };

  const sendSearchFilters = () => {
    if (filterValidation()) return;
    let finalFilter = attributeStates?.map((filter) =>
      prepareCriteriaForBE(AttributeFilters, filter)
    );
    if (!isEmpty(additionalFilterCriteria)) {
      finalFilter = finalFilter.concat(additionalFilterCriteria);
    }
    filterAction(finalFilter, maxResultFilter);
    onToggleHandler();
  };

  function getRowAttributeClass() {
    return attributeStates?.length > 0 ? 'max-height-scrollable-265' : 'h-100';
  }

  return (
    <FilterCard
      translations={t}
      activeKey={activeKey}
      onToggle={onToggleHandler}
    >
      <Row style={{ display: activeKey === '0' ? 'flex' : 'none' }}>
        <Col xs={12} md={4}>
          <Card className="card-border chosen-attributes">
            <InputField
              id="attribute-input"
              name="attribute-input"
              className="smaller-input"
              placeholder={Placeholders.attributes}
              onChange={onChangeFilterHandler}
              tooltipText={Placeholders.attributes}
              tooltipPlacement="top"
            />
            <Row
              md={columnsPerRow}
              className={`d-flex flex-column align-items-start justify-content-start max-height-scrollable-180`}
            >
              {finalAttributeList?.length > 0 ? (
                <AttributeCheckBox
                  attributeStates={attributeStates}
                  finalAttributeList={finalAttributeList}
                  onCheckBoxChangeHandler={onCheckBoxChangeHandler}
                />
              ) : (
                <Col md={12}>
                  <Alert
                    severity={notificationType.error}
                    message={
                      attributeList.length === 0
                        ? NotificationMessages.notExist
                        : NotificationMessages.notFound
                    }
                    className={'w-100 mb-2'}
                  />
                </Col>
              )}
            </Row>
          </Card>
        </Col>
        <Col md={8}>
          <Card className="card-border">
            <Row>
              <Card.Title className="d-flex gap-3 align-items-center mb-title pr-none">
                <div
                  className={
                    'underline pl-13 d-flex justify-content-between mr-16'
                  }
                >
                  {Placeholders.search}
                  {!inForm && showEditTableFiltersButton && (
                    <ModalDataWrapper
                      type={tableName}
                      attributeStates={attributeStates}
                      refresh={() => {
                        return typeof setRefreshState === 'function'
                          ? setRefreshState(!refreshState)
                          : () => {};
                      }}
                      filterButtonVisible
                    />
                  )}
                </div>
              </Card.Title>
            </Row>
            <Row
              className={`d-flex flex-column align-items-start justify-content-start ${getRowAttributeClass()}`}
            >
              {attributeStates?.length > 0 ? (
                attributeStates?.map((state) => (
                  <Row key={state?.attribute?.key}>
                    <AttributeItem
                      key={state?.attribute?.key}
                      state={state}
                      setState={setAttributeStates}
                      radioButtons={AttributeRadioButton}
                      enums={AttributeFilters}
                      setOpenedFilter={setOpenedFilter}
                      openedFilter={openedFilter}
                      t={t}
                    />
                  </Row>
                ))
              ) : (
                <Col>
                  <Alert
                    severity={notificationType.info}
                    message={NotificationMessages.notChosen}
                    className={'w-100 mb-2'}
                  />
                </Col>
              )}
            </Row>
            <Row>
              <Col className="px-0">
                <AttributeCardFooter
                  Placeholders={Placeholders}
                  maxResultFilter={maxResultFilter}
                  onChangeMaxResultFilterHandler={
                    onChangeMaxResultFilterHandler
                  }
                  sendSearchFilters={sendSearchFilters}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </FilterCard>
  );
};

export default withTranslations(AttributeView, 'AttributeView');
